import {
	ArrowLeft,
	Check,
	ChevronRight,
	Copy,
	Crown,
	ExternalLink,
	Image,
	Link2,
	Package,
	Pencil,
	Plus,
	Save,
	Search,
	Sparkles,
	Star,
	Store,
	Tag,
	Trash2,
	Unlink,
	X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api/client";
import { CategorySelectModal } from "../components/categories/CategorySelectModal";
import { AttributeCrawler } from "../components/products/AttributeCrawler";
import { ProductSeoHistory } from "../components/products/ProductSeoHistory";

// ---------------------------------------------------------------------------
// Types mirroring the server response
// ---------------------------------------------------------------------------
interface Merchant {
	id: number;
	name: string;
	website: string;
	logo: string | null;
	validated: boolean;
}

interface MerchantProductDetail {
	id: number;
	gtin: string;
	name: string;
	brand: string | null;
	color: string | null;
	gender: string | null;
	size: string | null;
	description: string | null;
	image: string | null;
	url: string;
	currentPrice: number | null;
	salePrice: number | null;
	shippingCost: number | null;
	availability: string;
	active: boolean;
	merchant: Merchant;
}

interface ProductDescription {
	id: number;
	language: string;
	description: string;
}

interface ProductImage {
	id: number;
	image_url: string;
	alt_text: string | null;
}

interface ProductCategory {
	id: number;
	name: string;
	google_id: number | null;
	parent: number | null;
}

interface ProductSeo {
	seo_title: string | null;
	seo_keywords: string | null;
	seo_description: string | null;
}

interface ProductVariant {
	id: number;
	gtin: string;
	name: string;
	url: string | null;
	isVariant: boolean;
	mainProductId: number | null;
	createdAt: string;
	updatedAt: string;
	lowestPrice: number | null;
	highestPrice: number | null;
	image: string | null;
	attributes: Record<string, string>;
}

interface VariantMerchantImage {
	variantId: number;
	variantName: string;
	mpId: number;
	mpName: string;
	image: string;
	currentPrice: number | null;
	merchantName: string;
}

interface ProductDetail {
	id: number;
	gtin: string;
	name: string;
	url: string | null;
	generatedContent: boolean;
	isVariant: boolean;
	mainProductId: number | null;
	createdAt: string;
	updatedAt: string;
	lowestPrice: number | null;
	highestPrice: number | null;
	descriptions: ProductDescription[];
	images: ProductImage[];
	attributes: Record<string, string>;
	seo: ProductSeo | null;
	categories: ProductCategory[];
	merchantProducts: MerchantProductDetail[];
	metadata: {
		title: string | null;
		description: string | null;
		tags: string | null;
		popularity_score: number | null;
	} | null;
}

// ---------------------------------------------------------------------------
// Editable field components
// ---------------------------------------------------------------------------
const inputClass =
	"px-3 py-1.5 rounded-lg border border-primary/30 bg-white text-sm text-slate-800 outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all";
const textareaClass = `${inputClass} resize-y min-h-[80px]`;
const labelClass =
	"text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1 block";
const Label = ({ children }: { children: React.ReactNode }) => (
	<span className={labelClass}>{children}</span>
);

const ATTRIBUTE_OPTIONS = [
	{ value: "brand", label: "Brand" },
	{ value: "color", label: "Color" },
	{ value: "size", label: "Size" },
	{ value: "gender", label: "Gender" },
	{ value: "delivery_info", label: "Delivery Info" },
	{ value: "material", label: "Material" },
	{ value: "weight", label: "Weight" },
	{ value: "dimensions", label: "Dimensions" },
	{ value: "warranty", label: "Warranty" },
	{ value: "condition", label: "Condition" },
	{ value: "model", label: "Model" },
	{ value: "manufacturer", label: "Manufacturer" },
	{ value: "energy_class", label: "Energy Class" },
	{ value: "sku", label: "SKU" },
	{ value: "mpn", label: "MPN" },
] as const;

const EditableField = ({
	label,
	value,
	editing,
	onChange,
	type = "text",
	multiline = false,
	placeholder,
}: {
	label: string;
	value: string;
	editing: boolean;
	onChange: (v: string) => void;
	type?: string;
	multiline?: boolean;
	placeholder?: string;
}) => (
	<div>
		<Label>{label}</Label>
		{editing ? (
			multiline ? (
				<textarea
					className={textareaClass}
					value={value}
					onChange={(e) => onChange(e.target.value)}
					placeholder={placeholder}
					rows={4}
				/>
			) : (
				<input
					type={type}
					className={inputClass}
					value={value}
					onChange={(e) => onChange(e.target.value)}
					placeholder={placeholder}
				/>
			)
		) : (
			<p
				className={`text-sm text-slate-700 ${!value ? "text-slate-400 italic" : ""}`}
			>
				{value || `No ${label.toLowerCase()}`}
			</p>
		)}
	</div>
);

const EditableToggle = ({
	label,
	value,
	editing,
	onChange,
}: {
	label: string;
	value: boolean;
	editing: boolean;
	onChange: (v: boolean) => void;
}) => (
	<div className="flex items-center gap-3">
		{editing ? (
			<button
				type="button"
				onClick={() => onChange(!value)}
				className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${value ? "bg-primary" : "bg-slate-200"}`}
			>
				<span
					className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${value ? "translate-x-5" : "translate-x-0"}`}
				/>
			</button>
		) : (
			<span
				className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${value ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-400"}`}
			>
				{value ? "Yes" : "No"}
			</span>
		)}
		<span className="text-sm text-slate-600">{label}</span>
	</div>
);

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------
const Section = ({
	title,
	icon: Icon,
	children,
	badge,
	actions,
}: {
	title: string;
	icon: React.ElementType;
	children: React.ReactNode;
	badge?: React.ReactNode;
	actions?: React.ReactNode;
}) => (
	<div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
		<div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
			<Icon className="w-4 h-4 text-slate-400" />
			<h2 className="text-sm font-semibold text-slate-700">{title}</h2>
			{badge}
			{actions && <div className="ml-auto">{actions}</div>}
		</div>
		<div className="p-6">{children}</div>
	</div>
);

// ---------------------------------------------------------------------------
// Product page
// ---------------------------------------------------------------------------
const Product = () => {
	const { id } = useParams<{ id: string }>();
	const [product, setProduct] = useState<ProductDetail | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [selectedImage, setSelectedImage] = useState(0);
	const [editingDetails, setEditingDetails] = useState(false);
	const [editingDescriptions, setEditingDescriptions] = useState(false);
	const [editingAttributes, setEditingAttributes] = useState(false);
	const [editingSeo, setEditingSeo] = useState(false);
	const [savingSection, setSavingSection] = useState<string | null>(null);

	// ── Editable state ──
	const [editName, setEditName] = useState("");
	const [editGtin, setEditGtin] = useState("");
	const [editUrl, setEditUrl] = useState("");
	const [editGenContent, setEditGenContent] = useState(false);
	const [editIsVariant, setEditIsVariant] = useState(false);
	const [editMainProductId, setEditMainProductId] = useState("");
	const [editSeoTitle, setEditSeoTitle] = useState("");
	const [editSeoKeywords, setEditSeoKeywords] = useState("");
	const [editSeoDescription, setEditSeoDescription] = useState("");
	const [editDescriptions, setEditDescriptions] = useState<
		{ language: string; description: string }[]
	>([]);
	const [editAttributes, setEditAttributes] = useState<
		{ key: string; value: string }[]
	>([]);

	// ── Category modal state ──
	const [showCategoryModal, setShowCategoryModal] = useState(false);
	const [addingCategory, setAddingCategory] = useState(false);

	// ── Variant state ──
	const [variants, setVariants] = useState<ProductVariant[]>([]);
	const [clusterMasterId, setClusterMasterId] = useState<number | null>(null);
	const [clusterMasterName, setClusterMasterName] = useState<string | null>(
		null,
	);
	const [clusterMasterImage, setClusterMasterImage] = useState<string | null>(
		null,
	);
	const [loadingVariants, setLoadingVariants] = useState(false);
	const [showAddVariant, setShowAddVariant] = useState(false);
	const [variantSearchQuery, setVariantSearchQuery] = useState("");
	const [variantSearchResults, setVariantSearchResults] = useState<
		{ id: number; gtin: string; name: string }[]
	>([]);
	const [searchingVariants, setSearchingVariants] = useState(false);

	// ── Variant merchant images state ──
	const [variantImages, setVariantImages] = useState<VariantMerchantImage[]>(
		[],
	);
	const [loadingVariantImages, setLoadingVariantImages] = useState(false);

	// ── Primary image state ──
	const [settingPrimaryImage, setSettingPrimaryImage] = useState(false);

	// ── Image dimensions state ──
	const [mpImgDims, setMpImgDims] = useState<
		Record<string | number, { w: number; h: number }>
	>({});
	const [imgDims, setImgDims] = useState<
		Record<number, { w: number; h: number }>
	>({});

	// ── Sync edit state from product ──
	const syncEditState = useCallback((p: ProductDetail) => {
		setEditName(p.name);
		setEditGtin(p.gtin);
		setEditUrl(p.url ?? "");
		setEditGenContent(p.generatedContent);
		setEditIsVariant(p.isVariant);
		setEditMainProductId(p.mainProductId?.toString() ?? "");
		setEditSeoTitle(p.seo?.seo_title ?? "");
		setEditSeoKeywords(p.seo?.seo_keywords ?? "");
		setEditSeoDescription(p.seo?.seo_description ?? "");
		setEditDescriptions(
			p.descriptions.map((d) => ({
				language: d.language,
				description: d.description,
			})),
		);
		setEditAttributes(
			Object.entries(p.attributes ?? {}).map(([k, v]) => ({
				key: k,
				value: String(v ?? ""),
			})),
		);
	}, []);

	// ── Fetch product ──
	const fetchProduct = useCallback(async () => {
		if (!id) return;
		setLoading(true);
		setError(null);
		try {
			const { data, error: err } = await api.products({ id }).get();
			if (err || !data) throw new Error("Product not found");
			const p = data as unknown as ProductDetail;
			setProduct(p);
			syncEditState(p);
			setSelectedImage(0);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to load product");
		} finally {
			setLoading(false);
		}
	}, [id, syncEditState]);

	useEffect(() => {
		fetchProduct();
	}, [fetchProduct]);

	// ── Fetch variants (cluster view) ──
	const fetchVariants = useCallback(async () => {
		if (!id || !product) return;
		setLoadingVariants(true);
		try {
			// Resolve the master product ID for the cluster
			const masterId =
				product.isVariant && product.mainProductId
					? product.mainProductId
					: Number(id);
			const masterIdStr = String(masterId);

			// Store master info
			setClusterMasterId(masterId);

			// If current product is a variant, fetch the master product's details for display
			if (masterId !== Number(id)) {
				try {
					const { data: masterData } = await api
						.products({ id: masterIdStr })
						.get();
					if (masterData) {
						const master = masterData as unknown as ProductDetail;
						setClusterMasterName(master.name);
						setClusterMasterImage(master.images?.[0]?.image_url ?? null);
					}
				} catch {
					setClusterMasterName(null);
					setClusterMasterImage(null);
				}
			} else {
				setClusterMasterName(null);
				setClusterMasterImage(null);
			}

			// Fetch variants from the master product
			const { data, error: err } = await api
				.products({ id: masterIdStr })
				.variants.get();
			if (err) throw new Error("Failed to load variants");
			const vars = (data as unknown as ProductVariant[]) ?? [];
			setVariants(vars);

			// Fetch merchant product images for each variant
			const allVariantIds = vars.map((v) => v.id);
			if (allVariantIds.length > 0) {
				setLoadingVariantImages(true);
				try {
					const results = await Promise.all(
						allVariantIds.map(async (vid) => {
							const variant = vars.find((v) => v.id === vid);
							const { data: vData } = await api
								.products({ id: String(vid) })
								.get();
							if (!vData) return [];
							const vDetail = vData as unknown as ProductDetail;
							return vDetail.merchantProducts
								.filter((mp) => mp.image)
								.map((mp) => ({
									variantId: vid,
									variantName: variant?.name ?? "",
									mpId: mp.id,
									mpName: mp.name,
									image: mp.image as string,
									currentPrice: mp.currentPrice,
									merchantName: mp.merchant.name,
								}));
						}),
					);
					setVariantImages(results.flat());
				} catch {
					// silently fail
				} finally {
					setLoadingVariantImages(false);
				}
			} else {
				setVariantImages([]);
			}
		} catch {
			// silently fail, variants section just won't show
		} finally {
			setLoadingVariants(false);
		}
	}, [id, product]);

	useEffect(() => {
		fetchVariants();
	}, [fetchVariants]);

	// ── Variant handlers ──
	const handleSearchVariants = async (query: string) => {
		setVariantSearchQuery(query);
		if (query.length < 2) {
			setVariantSearchResults([]);
			return;
		}
		setSearchingVariants(true);
		try {
			const { data } = await api.products.search.get({
				query: { q: query },
			});
			const results = data as unknown as {
				items: { id: number; gtin: string; name: string }[];
			};
			// Exclude current product and already-linked variants
			const existingIds = new Set([Number(id), ...variants.map((v) => v.id)]);
			setVariantSearchResults(
				(results?.items ?? []).filter((r) => !existingIds.has(r.id)),
			);
		} catch {
			setVariantSearchResults([]);
		} finally {
			setSearchingVariants(false);
		}
	};

	const handleAddVariant = async (variantId: number) => {
		if (!clusterMasterId) return;
		try {
			const { error: err } = await api
				.products({ id: String(clusterMasterId) })
				.variants({ variantId: String(variantId) })
				.post();
			if (err) throw new Error("Failed to add variant");
			setShowAddVariant(false);
			setVariantSearchQuery("");
			setVariantSearchResults([]);
			await fetchVariants();
		} catch {
			setError("Failed to add variant");
		}
	};

	const handleRemoveVariant = async (variantId: number) => {
		if (!clusterMasterId) return;
		try {
			const { error: err } = await api
				.products({ id: String(clusterMasterId) })
				.variants({ variantId: String(variantId) })
				.delete();
			if (err) throw new Error("Failed to remove variant");
			await fetchVariants();
		} catch {
			setError("Failed to remove variant");
		}
	};

	const handlePromoteVariant = async (variantId: number) => {
		if (!clusterMasterId) return;
		try {
			const { error: err } = await api
				.products({ id: String(clusterMasterId) })
				.variants({ variantId: String(variantId) })
				.promote.post();
			if (err) throw new Error("Failed to promote variant");
			// Refresh - if we promoted the current product, reload; otherwise just refresh variants
			await fetchProduct();
		} catch {
			setError("Failed to promote variant to master");
		}
	};

	// ── Primary image handler ──
	const handleSetPrimaryImage = async (imageUrl: string) => {
		if (!id) return;
		setSettingPrimaryImage(true);
		try {
			const { error: err } = await api
				.products({ id })
				["primary-image"].put({ imageUrl });
			if (err) throw new Error("Failed to set primary image");
			await fetchProduct();
		} catch {
			setError("Failed to set primary image");
		} finally {
			setSettingPrimaryImage(false);
		}
	};

	// ── Per-section save handlers ──
	const handleSaveDetails = async () => {
		if (!product || !id) return;
		setSavingSection("details");
		try {
			const { error: err } = await api.products({ id }).patch({
				product: {
					name: editName,
					gtin: editGtin,
					url: editUrl || null,
					generatedContent: editGenContent,
					isVariant: editIsVariant,
					mainProductId: editMainProductId ? Number(editMainProductId) : null,
				},
			});
			if (err) throw new Error("Save failed");
			setEditingDetails(false);
			await fetchProduct();
		} catch {
			setError("Failed to save details");
		} finally {
			setSavingSection(null);
		}
	};

	const handleSaveDescriptions = async () => {
		if (!product || !id) return;
		setSavingSection("descriptions");
		try {
			const { error: err } = await api.products({ id }).patch({
				descriptions: editDescriptions,
			});
			if (err) throw new Error("Save failed");
			setEditingDescriptions(false);
			await fetchProduct();
		} catch {
			setError("Failed to save descriptions");
		} finally {
			setSavingSection(null);
		}
	};

	const handleSaveAttributes = async () => {
		if (!product || !id) return;
		setSavingSection("attributes");
		try {
			const { error: err } = await api.products({ id }).patch({
				attributes: Object.fromEntries(
					editAttributes
						.filter((a) => a.key.trim())
						.map((a) => [a.key, a.value]),
				),
			});
			if (err) throw new Error("Save failed");
			setEditingAttributes(false);
			await fetchProduct();
		} catch {
			setError("Failed to save attributes");
		} finally {
			setSavingSection(null);
		}
	};

	const handleSaveSeo = async () => {
		if (!product || !id) return;
		setSavingSection("seo");
		try {
			const { error: err } = await api.products({ id }).patch({
				seo: {
					seoTitle: editSeoTitle || null,
					seoKeywords: editSeoKeywords || null,
					seoDescription: editSeoDescription || null,
				},
			});
			if (err) throw new Error("Save failed");
			setEditingSeo(false);
			await fetchProduct();
		} catch {
			setError("Failed to save SEO");
		} finally {
			setSavingSection(null);
		}
	};

	// ── Per-section cancel handlers ──
	const handleCancelDetails = () => {
		if (product) {
			setEditName(product.name);
			setEditGtin(product.gtin);
			setEditUrl(product.url ?? "");
			setEditGenContent(product.generatedContent);
			setEditIsVariant(product.isVariant);
			setEditMainProductId(product.mainProductId?.toString() ?? "");
		}
		setEditingDetails(false);
	};

	const handleCancelDescriptions = () => {
		if (product) {
			setEditDescriptions(
				product.descriptions.map((d) => ({
					language: d.language,
					description: d.description,
				})),
			);
		}
		setEditingDescriptions(false);
	};

	const handleCancelAttributes = () => {
		if (product) {
			setEditAttributes(
				Object.entries(product.attributes ?? {}).map(([k, v]) => ({
					key: k,
					value: String(v ?? ""),
				})),
			);
		}
		setEditingAttributes(false);
	};

	const handleCancelSeo = () => {
		if (product) {
			setEditSeoTitle(product.seo?.seo_title ?? "");
			setEditSeoKeywords(product.seo?.seo_keywords ?? "");
			setEditSeoDescription(product.seo?.seo_description ?? "");
		}
		setEditingSeo(false);
	};

	// ── Section edit action buttons ──
	const sectionEditActions = (
		isEditing: boolean,
		isSaving: boolean,
		onEdit: () => void,
		onSave: () => void,
		onCancel: () => void,
	) =>
		isEditing ? (
			<div className="flex items-center gap-2">
				<button
					type="button"
					onClick={onCancel}
					className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
				>
					<X className="w-3.5 h-3.5" /> Cancel
				</button>
				<button
					type="button"
					onClick={onSave}
					disabled={isSaving}
					className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-black bg-brand hover:bg-brand-hover transition-colors disabled:opacity-50"
				>
					{isSaving ? (
						<div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
					) : (
						<Save className="w-3.5 h-3.5" />
					)}
					Save
				</button>
			</div>
		) : (
			<button
				type="button"
				onClick={onEdit}
				className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
			>
				<Pencil className="w-3.5 h-3.5" /> Edit
			</button>
		);

	// ── Add category with full ancestor chain ──
	const handleAddCategory = async (categoryId: number) => {
		if (!product || !id) return;
		setAddingCategory(true);
		try {
			const existingIds = product.categories.map((c) => c.id);
			const allIds = existingIds.includes(categoryId)
				? existingIds
				: [...existingIds, categoryId];

			const { error: err } = await api
				.products({ id })
				.patch({ categories: allIds });
			if (err) throw new Error("Failed to add category");

			setShowCategoryModal(false);
			await fetchProduct();
		} catch {
			setError("Failed to add category");
		} finally {
			setAddingCategory(false);
		}
	};

	// ── Remove a single category ──
	const handleRemoveCategory = async (categoryId: number) => {
		if (!product || !id) return;
		const remaining = product.categories
			.filter((c) => c.id !== categoryId)
			.map((c) => c.id);
		try {
			const { error: err } = await api
				.products({ id })
				.patch({ categories: remaining });
			if (err) throw new Error("Failed to remove category");
			await fetchProduct();
		} catch {
			setError("Failed to remove category");
		}
	};

	// ── Build hierarchical category structure for display ──
	const buildCategoryHierarchy = (cats: ProductCategory[]) => {
		const catMap = new Map(cats.map((c) => [c.id, c]));
		const childrenOf = new Map<number, ProductCategory[]>();

		for (const cat of cats) {
			if (cat.parent && catMap.has(cat.parent)) {
				const list = childrenOf.get(cat.parent) ?? [];
				list.push(cat);
				childrenOf.set(cat.parent, list);
			}
		}

		// Roots = categories whose parent is null or whose parent is NOT in our set
		const roots: ProductCategory[] = [];
		for (const cat of cats) {
			if (!cat.parent || !catMap.has(cat.parent)) {
				roots.push(cat);
			}
		}

		// Build a flat list with depth info
		type CatWithDepth = ProductCategory & { depth: number };
		const buildChain = (parentId: number, depth: number): CatWithDepth[] => {
			const kids = childrenOf.get(parentId) ?? [];
			const result: CatWithDepth[] = [];
			for (const kid of kids) {
				result.push({ ...kid, depth });
				result.push(...buildChain(kid.id, depth + 1));
			}
			return result;
		};

		return roots.map((root) => ({
			cat: root,
			children: buildChain(root.id, 1),
		}));
	};

	// ── Loading / error states ──
	if (loading) {
		return (
			<div className="flex items-center justify-center py-32">
				<div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
			</div>
		);
	}

	if (error || !product) {
		return (
			<div className="flex flex-col items-center justify-center py-32 text-slate-400">
				<Package className="w-16 h-16 mb-4 text-slate-300" />
				<p className="text-lg font-semibold text-slate-500">
					{error ?? "Product not found"}
				</p>
				<Link
					to="/products"
					className="mt-4 text-sm text-primary hover:underline flex items-center gap-1"
				>
					<ArrowLeft className="w-4 h-4" /> Back to products
				</Link>
			</div>
		);
	}

	const primaryImage =
		product.images.length > 0 ? product.images[selectedImage]?.image_url : null;

	const priceRange =
		product.lowestPrice != null &&
		product.highestPrice != null &&
		product.lowestPrice !== product.highestPrice
			? `${product.lowestPrice.toLocaleString("da-DK")} – ${product.highestPrice.toLocaleString("da-DK")}`
			: product.lowestPrice != null
				? product.lowestPrice.toLocaleString("da-DK")
				: null;

	return (
		<div className="space-y-6">
			{/* Breadcrumb */}
			<div className="flex items-center gap-2 text-sm text-slate-400">
				<Link to="/products" className="hover:text-primary transition-colors">
					Products
				</Link>
				<ChevronRight className="w-3.5 h-3.5" />
				<span className="text-slate-600 font-medium truncate max-w-md">
					{product.name}
				</span>
			</div>

			{/* ────────── Hero: Image + Core Info ────────── */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
				{/* Image gallery */}
				<div className="space-y-4">
					<div className="relative aspect-square bg-white rounded-2xl border border-slate-200 flex items-center justify-center overflow-hidden">
						{primaryImage ? (
							<img
								src={primaryImage}
								alt={product.name}
								className="w-full h-full object-contain p-6"
							/>
						) : (
							<Package className="w-20 h-20 text-slate-300" />
						)}
						{product.generatedContent && (
							<span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary">
								<Sparkles className="w-3.5 h-3.5" /> AI Refined
							</span>
						)}
					</div>
					{product.images.length > 1 && (
						<div className="flex gap-2 overflow-x-auto pb-1">
							{product.images.map((img, i) => (
								<button
									key={img.id}
									type="button"
									onClick={() => setSelectedImage(i)}
									className={`shrink-0 w-16 h-16 rounded-lg border-2 overflow-hidden transition-all ${
										i === selectedImage
											? "border-primary shadow-md shadow-primary/20"
											: "border-slate-200 hover:border-slate-300"
									}`}
								>
									<img
										src={img.image_url}
										alt={img.alt_text ?? ""}
										className="w-full h-full object-contain p-1"
									/>
								</button>
							))}
						</div>
					)}
				</div>

				{/* Core product info */}
				<div className="space-y-6">
					<Section
						title="Product Details"
						icon={Package}
						actions={sectionEditActions(
							editingDetails,
							savingSection === "details",
							() => {
								if (product) {
									setEditName(product.name);
									setEditGtin(product.gtin);
									setEditUrl(product.url ?? "");
									setEditGenContent(product.generatedContent);
									setEditIsVariant(product.isVariant);
									setEditMainProductId(product.mainProductId?.toString() ?? "");
								}
								setEditingDetails(true);
							},
							handleSaveDetails,
							handleCancelDetails,
						)}
					>
						<div className="space-y-4">
							<div>
								<Label>ID</Label>
								<p className="text-sm text-slate-700 font-mono">{product.id}</p>
							</div>
							<EditableField
								label="Name"
								value={editingDetails ? editName : product.name}
								editing={editingDetails}
								onChange={setEditName}
								placeholder="Product name"
							/>
							<div className="grid grid-cols-2 gap-4">
								<EditableField
									label="GTIN"
									value={editingDetails ? editGtin : product.gtin}
									editing={editingDetails}
									onChange={setEditGtin}
									placeholder="GTIN / EAN"
								/>
								<EditableField
									label="URL Slug"
									value={editingDetails ? editUrl : (product.url ?? "")}
									editing={editingDetails}
									onChange={setEditUrl}
									placeholder="product-url-slug"
								/>
							</div>
							<div className="grid grid-cols-2 gap-4">
								<EditableToggle
									label="AI Generated Content"
									value={
										editingDetails ? editGenContent : product.generatedContent
									}
									editing={editingDetails}
									onChange={setEditGenContent}
								/>
								<EditableToggle
									label="Is Variant"
									value={editingDetails ? editIsVariant : product.isVariant}
									editing={editingDetails}
									onChange={setEditIsVariant}
								/>
							</div>
							{(editingDetails || product.mainProductId) && (
								<EditableField
									label="Main Product ID"
									value={
										editingDetails
											? editMainProductId
											: (product.mainProductId?.toString() ?? "")
									}
									editing={editingDetails}
									onChange={setEditMainProductId}
									type="number"
									placeholder="Parent product ID"
								/>
							)}
							{priceRange && (
								<div>
									<Label>Price Range</Label>
									<p className="text-xl font-bold text-slate-800">
										DKK {priceRange}
									</p>
									<p className="text-xs text-slate-400 mt-0.5">
										From {product.merchantProducts.length} merchant
										{product.merchantProducts.length !== 1 && "s"} · Computed
										from offers
									</p>
								</div>
							)}
							<div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-100">
								<div>
									<Label>Created</Label>
									<p className="text-xs text-slate-500">
										{new Date(product.createdAt).toLocaleString("da-DK")}
									</p>
								</div>
								<div>
									<Label>Updated</Label>
									<p className="text-xs text-slate-500">
										{new Date(product.updatedAt).toLocaleString("da-DK")}
									</p>
								</div>
							</div>
						</div>
					</Section>
				</div>
			</div>

			{/* ────────── Descriptions ────────── */}
			<Section
				title="Descriptions"
				icon={Tag}
				badge={
					<span className="text-xs font-normal text-slate-400 ml-1">
						(
						{
							(editingDescriptions ? editDescriptions : product.descriptions)
								.length
						}
						)
					</span>
				}
				actions={sectionEditActions(
					editingDescriptions,
					savingSection === "descriptions",
					() => {
						if (product) {
							setEditDescriptions(
								product.descriptions.map((d) => ({
									language: d.language,
									description: d.description,
								})),
							);
						}
						setEditingDescriptions(true);
					},
					handleSaveDescriptions,
					handleCancelDescriptions,
				)}
			>
				{editingDescriptions ? (
					<div className="space-y-4">
						{editDescriptions.map((desc, i) => (
							<div
								key={`desc-${desc.language}-${i}`}
								className="border border-slate-200 rounded-lg p-4 space-y-3"
							>
								<div className="flex items-center justify-between">
									<input
										className="text-xs font-semibold uppercase tracking-wider bg-slate-50 border border-slate-200 rounded px-2 py-1 w-20 text-center"
										value={desc.language}
										onChange={(e) => {
											const updated = [...editDescriptions];
											updated[i] = { ...updated[i], language: e.target.value };
											setEditDescriptions(updated);
										}}
									/>
									<button
										type="button"
										onClick={() =>
											setEditDescriptions(
												editDescriptions.filter((_, idx) => idx !== i),
											)
										}
										className="text-slate-400 hover:text-danger transition-colors"
									>
										<Trash2 className="w-4 h-4" />
									</button>
								</div>
								<textarea
									className={textareaClass}
									value={desc.description}
									onChange={(e) => {
										const updated = [...editDescriptions];
										updated[i] = { ...updated[i], description: e.target.value };
										setEditDescriptions(updated);
									}}
									rows={6}
								/>
							</div>
						))}
						<button
							type="button"
							onClick={() =>
								setEditDescriptions([
									...editDescriptions,
									{ language: "da-dk", description: "" },
								])
							}
							className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:text-primary-dark transition-colors"
						>
							<Plus className="w-4 h-4" /> Add Description
						</button>
					</div>
				) : product.descriptions.length > 0 ? (
					<div className="space-y-4">
						{product.descriptions.map((desc) => (
							<div key={desc.id}>
								<span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full uppercase mb-2 inline-block">
									{desc.language}
								</span>
								<div
									className="prose prose-sm prose-slate max-w-none text-slate-600"
									/* biome-ignore lint/security/noDangerouslySetInnerHtml: server HTML */
									dangerouslySetInnerHTML={{ __html: desc.description }}
								/>
							</div>
						))}
					</div>
				) : (
					<p className="text-sm text-slate-400 italic">No descriptions</p>
				)}
			</Section>

			{/* ────────── Attributes ────────── */}
			<Section
				title="Attributes"
				icon={Tag}
				badge={
					<span className="text-xs font-normal text-slate-400 ml-1">
						(
						{
							(editingAttributes
								? editAttributes
								: Object.keys(product.attributes)
							).length
						}
						)
					</span>
				}
				actions={sectionEditActions(
					editingAttributes,
					savingSection === "attributes",
					() => {
						console.log(
							product.attributes,
							Object.entries(product.attributes ?? {}).map(([k, v]) => ({
								key: k,
								value: String(v ?? ""),
							})),
						);
						if (product) {
							setEditAttributes(
								Object.entries(product.attributes ?? {}).map(([k, v]) => ({
									key: k,
									value: String(v ?? ""),
								})),
							);
						}
						setEditingAttributes(true);
					},
					handleSaveAttributes,
					handleCancelAttributes,
				)}
			>
				{editingAttributes ? (
					<div className="space-y-2">
						{editAttributes.map((attr, i) => {
							const usedKeys = new Set(
								editAttributes.filter((_, idx) => idx !== i).map((a) => a.key),
							);
							const availableOptions = ATTRIBUTE_OPTIONS.filter(
								(opt) => !usedKeys.has(opt.value),
							);
							const isKnownKey = ATTRIBUTE_OPTIONS.some(
								(opt) => opt.value === attr.key,
							);
							return (
								<div
									key={`attr-${attr.key || i}`}
									className="flex items-center gap-2"
								>
									<select
										className={`${inputClass} w-1/3`}
										value={attr.key}
										onChange={(e) => {
											const updated = [...editAttributes];
											updated[i] = { ...updated[i], key: e.target.value };
											setEditAttributes(updated);
										}}
									>
										<option value="" disabled>
											Select attribute…
										</option>
										{!isKnownKey && attr.key && (
											<option value={attr.key}>{attr.key} (custom)</option>
										)}
										{availableOptions.map((opt) => (
											<option key={opt.value} value={opt.value}>
												{opt.label}
											</option>
										))}
									</select>
									<input
										className={`${inputClass} flex-1`}
										value={attr.value}
										placeholder="Value"
										onChange={(e) => {
											const updated = [...editAttributes];
											updated[i] = { ...updated[i], value: e.target.value };
											setEditAttributes(updated);
										}}
									/>
									<button
										type="button"
										onClick={() =>
											setEditAttributes(
												editAttributes.filter((_, idx) => idx !== i),
											)
										}
										className="text-slate-400 hover:text-danger transition-colors p-1"
									>
										<Trash2 className="w-4 h-4" />
									</button>
								</div>
							);
						})}
						{(() => {
							const allUsed = new Set(editAttributes.map((a) => a.key));
							const nextAvailable = ATTRIBUTE_OPTIONS.find(
								(opt) => !allUsed.has(opt.value),
							);
							if (!nextAvailable) return null;
							return (
								<button
									type="button"
									onClick={() =>
										setEditAttributes([
											...editAttributes,
											{ key: nextAvailable.value, value: "" },
										])
									}
									className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:text-primary-dark transition-colors mt-2"
								>
									<Plus className="w-4 h-4" /> Add Attribute
								</button>
							);
						})()}
					</div>
				) : Object.keys(product.attributes).length > 0 ? (
					<div className="grid grid-cols-2 md:grid-cols-3 gap-2">
						{Object.entries(product.attributes).map(([key, value]) => (
							<div
								key={key}
								className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2"
							>
								<p className="text-[11px] text-slate-400 capitalize">{key}</p>
								<p className="text-sm font-medium text-slate-700">{value}</p>
							</div>
						))}
					</div>
				) : (
					<p className="text-sm text-slate-400 italic">No attributes</p>
				)}
			</Section>

			<AttributeCrawler productId={product.id} onSaved={fetchProduct} />

			{/* ────────── SEO ────────── */}
			<Section
				title="SEO"
				icon={Tag}
				actions={sectionEditActions(
					editingSeo,
					savingSection === "seo",
					() => {
						if (product) {
							setEditSeoTitle(product.seo?.seo_title ?? "");
							setEditSeoKeywords(product.seo?.seo_keywords ?? "");
							setEditSeoDescription(product.seo?.seo_description ?? "");
						}
						setEditingSeo(true);
					},
					handleSaveSeo,
					handleCancelSeo,
				)}
			>
				<div className="space-y-4">
					<EditableField
						label="SEO Title"
						value={editingSeo ? editSeoTitle : (product.seo?.seo_title ?? "")}
						editing={editingSeo}
						onChange={setEditSeoTitle}
						placeholder="Page title for search engines"
					/>
					<EditableField
						label="SEO Description"
						value={
							editingSeo
								? editSeoDescription
								: (product.seo?.seo_description ?? "")
						}
						editing={editingSeo}
						onChange={setEditSeoDescription}
						multiline
						placeholder="Meta description for search engines"
					/>
					<div>
						<Label>SEO Keywords</Label>
						{editingSeo ? (
							<textarea
								className={textareaClass}
								value={editSeoKeywords}
								onChange={(e) => setEditSeoKeywords(e.target.value)}
								placeholder="keyword1, keyword2, keyword3"
								rows={3}
							/>
						) : product.seo?.seo_keywords ? (
							<div className="flex flex-wrap gap-1">
								{product.seo.seo_keywords.split(",").map((kw) => (
									<span
										key={kw.trim()}
										className="text-[11px] bg-slate-100 text-slate-500 px-2.5 py-0.5 rounded-full"
									>
										{kw.trim()}
									</span>
								))}
							</div>
						) : (
							<p className="text-sm text-slate-400 italic">No keywords</p>
						)}
					</div>
				</div>
			</Section>

			{/* ────────── SEO history ────────── */}
			<ProductSeoHistory productId={product.id} />

			{/* ────────── Categories ────────── */}
			<Section
				title="Categories"
				icon={Tag}
				badge={
					<span className="text-xs font-normal text-slate-400 ml-1">
						({product.categories.length})
					</span>
				}
				actions={
					<button
						type="button"
						onClick={() => setShowCategoryModal(true)}
						className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-black bg-brand hover:bg-brand-hover transition-colors"
					>
						<Plus className="w-3.5 h-3.5" /> Add to Category
					</button>
				}
			>
				{product.categories.length > 0 ? (
					<div className="space-y-3">
						{buildCategoryHierarchy(product.categories).map((group) => (
							<div
								key={group.cat.id}
								className="border border-slate-100 rounded-lg overflow-hidden"
							>
								{/* Root category */}
								<div className="flex items-center justify-between px-4 py-2.5 bg-slate-50">
									<div className="flex items-center gap-2">
										<Tag className="w-3.5 h-3.5 text-primary" />
										<span className="text-sm font-semibold text-slate-700">
											{group.cat.name}
										</span>
										{group.cat.google_id && (
											<span className="text-[10px] text-slate-400">
												#{group.cat.google_id}
											</span>
										)}
									</div>
									<button
										type="button"
										onClick={() => handleRemoveCategory(group.cat.id)}
										className="text-slate-300 hover:text-danger transition-colors"
										title="Remove category"
									>
										<X className="w-3.5 h-3.5" />
									</button>
								</div>
								{/* Child categories (indented) */}
								{group.children.length > 0 && (
									<div className="px-4 py-2 space-y-1">
										{group.children.map((child) => (
											<div
												key={child.id}
												className="flex items-center justify-between"
												style={{ paddingLeft: `${child.depth * 16}px` }}
											>
												<div className="flex items-center gap-2 py-1">
													<ChevronRight className="w-3 h-3 text-slate-300" />
													<span className="text-xs text-slate-500">
														{child.name}
													</span>
													{child.google_id && (
														<span className="text-[10px] text-slate-300">
															#{child.google_id}
														</span>
													)}
												</div>
												<button
													type="button"
													onClick={() => handleRemoveCategory(child.id)}
													className="text-slate-200 hover:text-danger transition-colors"
													title="Remove category"
												>
													<X className="w-3 h-3" />
												</button>
											</div>
										))}
									</div>
								)}
							</div>
						))}
					</div>
				) : (
					<p className="text-sm text-slate-400 italic">
						No categories assigned
					</p>
				)}
			</Section>

			{/* ────────── Category Modal (Browse + Search) ────────── */}
			<CategorySelectModal
				open={showCategoryModal}
				onClose={() => setShowCategoryModal(false)}
				onSelect={handleAddCategory}
				excludeIds={product.categories.map((c) => c.id)}
				title="Add to Category"
				selecting={addingCategory}
			/>

			{/* ────────── Variants ────────── */}
			<Section
				title="Variant Cluster"
				icon={Copy}
				badge={
					<span className="text-xs font-normal text-slate-400 ml-1">
						({variants.length + (clusterMasterId ? 1 : 0)})
					</span>
				}
				actions={
					<button
						type="button"
						onClick={() => setShowAddVariant(!showAddVariant)}
						className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-black bg-brand hover:bg-brand-hover transition-colors"
					>
						{showAddVariant ? (
							<>
								<X className="w-3.5 h-3.5" /> Close
							</>
						) : (
							<>
								<Link2 className="w-3.5 h-3.5" /> Link Variant
							</>
						)}
					</button>
				}
			>
				{/* Add variant search */}
				{showAddVariant && (
					<div className="mb-4 border border-primary/20 rounded-lg p-4 bg-primary/5">
						<div className="relative">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
							<input
								type="text"
								className={`${inputClass} w-full pl-9`}
								placeholder="Search by product name or GTIN..."
								value={variantSearchQuery}
								onChange={(e) => handleSearchVariants(e.target.value)}
							/>
							{searchingVariants && (
								<div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-slate-300 border-t-primary rounded-full animate-spin" />
							)}
						</div>
						{variantSearchResults.length > 0 && (
							<div className="mt-2 max-h-48 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg bg-white">
								{variantSearchResults.map((result) => (
									<button
										key={result.id}
										type="button"
										onClick={() => handleAddVariant(result.id)}
										className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors flex items-center justify-between"
									>
										<div>
											<p className="text-sm font-medium text-slate-700 truncate">
												{result.name}
											</p>
											<p className="text-xs text-slate-400">
												ID: {result.id} · GTIN: {result.gtin}
											</p>
										</div>
										<Plus className="w-4 h-4 text-primary shrink-0" />
									</button>
								))}
							</div>
						)}
						{variantSearchQuery.length >= 2 &&
							variantSearchResults.length === 0 &&
							!searchingVariants && (
								<p className="text-xs text-slate-400 mt-2">
									No matching products found
								</p>
							)}
					</div>
				)}

				{loadingVariants ? (
					<div className="flex items-center justify-center py-8">
						<div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
					</div>
				) : clusterMasterId ? (
					<div className="divide-y divide-slate-100 -mx-6 -mb-6">
						{/* ── Master product row ── */}
						{(() => {
							const isSelf = clusterMasterId === Number(id);
							return (
								<div
									className={`flex items-center gap-4 px-6 py-3.5 transition-colors ${isSelf ? "bg-primary/5" : "hover:bg-amber-50/50 bg-amber-50/30"}`}
								>
									{/* Thumbnail */}
									<div
										className={`w-12 h-12 rounded-lg border shrink-0 flex items-center justify-center overflow-hidden ${isSelf ? "border-primary/30 bg-primary/5" : "border-amber-200 bg-amber-50"}`}
									>
										{isSelf && product.images?.[0]?.image_url ? (
											<img
												src={product.images[0].image_url}
												alt={product.name}
												className="w-full h-full object-contain p-1"
											/>
										) : clusterMasterImage ? (
											<img
												src={clusterMasterImage}
												alt={clusterMasterName ?? ""}
												className="w-full h-full object-contain p-1"
											/>
										) : (
											<Package
												className={`w-5 h-5 ${isSelf ? "text-primary/40" : "text-amber-400"}`}
											/>
										)}
									</div>
									{/* Info */}
									<div className="flex-1 min-w-0">
										{isSelf ? (
											<span className="text-sm font-medium text-slate-700 truncate block">
												{product.name}
											</span>
										) : (
											<Link
												to={`/products/${clusterMasterId}`}
												className="text-sm font-medium text-slate-700 hover:text-primary transition-colors truncate block"
											>
												{clusterMasterName ?? `Product #${clusterMasterId}`}
											</Link>
										)}
										<span className="text-xs text-slate-400">
											ID: {clusterMasterId}
										</span>
									</div>
									{/* Master badge */}
									<span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-700 shrink-0">
										<Crown className="w-3 h-3" /> Master
									</span>
									{isSelf && (
										<span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary shrink-0">
											Current
										</span>
									)}
									{!isSelf && (
										<ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
									)}
								</div>
							);
						})()}

						{/* ── Variant rows ── */}
						{variants.map((variant) => {
							const isSelf = variant.id === Number(id);
							return (
								<div
									key={variant.id}
									className={`flex items-center gap-4 px-6 py-3.5 transition-colors ${isSelf ? "bg-primary/5" : "hover:bg-slate-50/50"}`}
								>
									{/* Thumbnail */}
									<div
										className={`w-12 h-12 rounded-lg border bg-white shrink-0 flex items-center justify-center overflow-hidden ${isSelf ? "border-primary/30" : "border-slate-200"}`}
									>
										{variant.image ? (
											<img
												src={variant.image}
												alt={variant.name}
												className="w-full h-full object-contain p-1"
											/>
										) : (
											<Package className="w-5 h-5 text-slate-300" />
										)}
									</div>
									{/* Info */}
									<div className="flex-1 min-w-0">
										{isSelf ? (
											<span className="text-sm font-medium text-slate-700 truncate block">
												{variant.name}
											</span>
										) : (
											<Link
												to={`/products/${variant.id}`}
												className="text-sm font-medium text-slate-700 hover:text-primary transition-colors truncate block"
											>
												{variant.name}
											</Link>
										)}
										<div className="flex items-center gap-3 mt-0.5">
											<span className="text-xs text-slate-400">
												ID: {variant.id}
											</span>
											<span className="text-xs text-slate-400">
												GTIN: {variant.gtin}
											</span>
											{variant.attributes.color && (
												<span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
													{variant.attributes.color}
												</span>
											)}
											{variant.attributes.size && (
												<span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
													{variant.attributes.size}
												</span>
											)}
										</div>
									</div>
									{/* Current badge */}
									{isSelf && (
										<span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary shrink-0">
											Current
										</span>
									)}
									{/* Price */}
									<div className="text-right shrink-0">
										{variant.lowestPrice != null ? (
											<p className="text-sm font-bold text-slate-700">
												DKK {variant.lowestPrice.toLocaleString("da-DK")}
											</p>
										) : (
											<p className="text-xs text-slate-400">No price</p>
										)}
									</div>
									{/* Set as master */}
									{!isSelf && (
										<button
											type="button"
											onClick={() => handlePromoteVariant(variant.id)}
											className="text-slate-400 hover:text-amber-600 transition-colors p-1"
											title="Promote to master"
										>
											<Crown className="w-4 h-4" />
										</button>
									)}
									{/* Remove */}
									<button
										type="button"
										onClick={() => handleRemoveVariant(variant.id)}
										className="text-slate-300 hover:text-danger transition-colors p-1"
										title="Remove variant"
									>
										<Unlink className="w-4 h-4" />
									</button>
								</div>
							);
						})}
					</div>
				) : (
					<p className="text-sm text-slate-400 italic">
						No variants linked to this product
					</p>
				)}
			</Section>

			{/* ────────── Merchant Offers ────────── */}
			{product.merchantProducts.length > 0 && (
				<Section
					title="Merchant Offers"
					icon={Store}
					badge={
						<span className="text-xs font-normal text-slate-400 ml-1">
							({product.merchantProducts.length})
						</span>
					}
				>
					<div className="-m-6 overflow-x-auto">
						<table className="w-full text-sm">
							<thead>
								<tr className="bg-slate-50 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
									<th className="px-6 py-3">Merchant</th>
									<th className="px-6 py-3">Product Name</th>
									<th className="px-6 py-3">Brand</th>
									<th className="px-6 py-3">Price</th>
									<th className="px-6 py-3">Shipping</th>
									<th className="px-6 py-3">Availability</th>
									<th className="px-6 py-3">Status</th>
									<th className="px-6 py-3" />
								</tr>
							</thead>
							<tbody className="divide-y divide-slate-100">
								{product.merchantProducts.map((mp) => (
									<tr
										key={mp.id}
										className="hover:bg-slate-50/50 transition-colors"
									>
										<td className="px-6 py-3.5">
											<div className="flex items-center gap-3">
												{mp.merchant.logo ? (
													<img
														src={mp.merchant.logo}
														alt={mp.merchant.name}
														className="w-8 h-8 rounded-lg object-contain border border-slate-100"
													/>
												) : (
													<div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
														<Store className="w-4 h-4 text-slate-400" />
													</div>
												)}
												<div>
													<p className="font-medium text-slate-700">
														{mp.merchant.name}
													</p>
													{mp.merchant.validated && (
														<span className="text-[10px] text-success flex items-center gap-0.5">
															<Check className="w-3 h-3" /> Verified
														</span>
													)}
												</div>
											</div>
										</td>
										<td className="px-6 py-3.5 text-slate-600 max-w-50 truncate">
											{mp.name}
										</td>
										<td className="px-6 py-3.5 text-slate-500">
											{mp.brand ?? "–"}
										</td>
										<td className="px-6 py-3.5">
											<p className="font-bold text-slate-800">
												DKK {mp.currentPrice?.toLocaleString("da-DK") ?? "–"}
											</p>
											{mp.salePrice != null &&
												mp.salePrice !== mp.currentPrice && (
													<p className="text-[11px] text-danger line-through">
														DKK {mp.salePrice.toLocaleString("da-DK")}
													</p>
												)}
										</td>
										<td className="px-6 py-3.5 text-slate-500">
											{mp.shippingCost != null
												? `DKK ${mp.shippingCost.toLocaleString("da-DK")}`
												: "–"}
										</td>
										<td className="px-6 py-3.5">
											<span
												className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
													mp.availability === "InStock"
														? "bg-success/10 text-success"
														: "bg-slate-100 text-slate-400"
												}`}
											>
												{mp.availability === "InStock"
													? "In Stock"
													: "Out of Stock"}
											</span>
										</td>
										<td className="px-6 py-3.5">
											<span
												className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
													mp.active
														? "bg-success/10 text-success"
														: "bg-slate-100 text-slate-400"
												}`}
											>
												{mp.active ? "Active" : "Inactive"}
											</span>
										</td>
										<td className="px-6 py-3.5">
											<a
												href={mp.url}
												target="_blank"
												rel="noopener noreferrer"
												className="text-primary hover:text-primary-dark transition-colors"
											>
												<ExternalLink className="w-4 h-4" />
											</a>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</Section>
			)}

			{/* ────────── Images listing ────────── */}
			{product.images.length > 0 && (
				<Section
					title="Images"
					icon={Package}
					badge={
						<span className="text-xs font-normal text-slate-400 ml-1">
							({product.images.length})
						</span>
					}
				>
					<div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
						{product.images.map((img) => (
							<div
								key={img.id}
								className="aspect-square rounded-lg border border-slate-200 overflow-hidden bg-slate-50 relative group"
							>
								<img
									src={img.image_url}
									alt={img.alt_text ?? ""}
									className="w-full h-full object-contain p-2"
									onLoad={(e) => {
										const el = e.currentTarget;
										setImgDims((prev) => ({
											...prev,
											[img.id]: { w: el.naturalWidth, h: el.naturalHeight },
										}));
									}}
								/>
								{imgDims[img.id] && (
									<span className="absolute top-1 left-1 bg-slate-700/80 text-white text-[8px] leading-none px-1 py-0.5 rounded font-mono whitespace-nowrap">
										{imgDims[img.id].w}×{imgDims[img.id].h}
									</span>
								)}
								{img.alt_text && (
									<div className="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
										<p className="text-[10px] text-white truncate">
											{img.alt_text}
										</p>
									</div>
								)}
							</div>
						))}
					</div>
				</Section>
			)}

			{/* ────────── All Merchant Product Images ────────── */}
			{product.merchantProducts.length > 0 && (
				<Section
					title="All Merchant Images"
					icon={Image}
					badge={
						<span className="text-xs font-normal text-slate-400 ml-1">
							({product.merchantProducts.filter((mp) => mp.image).length} images
							from {product.merchantProducts.length} merchants)
						</span>
					}
				>
					<p className="text-xs text-slate-400 mb-4">
						Click an image to set it as the primary product image.
					</p>
					<div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
						{product.merchantProducts
							.filter((mp) => mp.image)
							.map((mp) => {
								const isPrimary =
									product.images.length > 0 &&
									product.images[0].image_url === mp.image;
								return (
									<button
										key={mp.id}
										type="button"
										disabled={settingPrimaryImage}
										onClick={() => {
											if (!isPrimary && mp.image) {
												handleSetPrimaryImage(mp.image);
											}
										}}
										className={`aspect-square rounded-lg border-2 overflow-hidden bg-white relative group transition-all ${
											isPrimary
												? "border-primary shadow-md shadow-primary/20"
												: "border-slate-200 hover:border-primary/50 hover:shadow-sm"
										} ${settingPrimaryImage ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
									>
										<img
											src={mp.image ?? ""}
											alt={mp.name ?? ""}
											className="w-full h-full object-contain p-2"
											onLoad={(e) => {
												const img = e.currentTarget;
												setMpImgDims((prev) => ({
													...prev,
													[mp.id]: {
														w: img.naturalWidth,
														h: img.naturalHeight,
													},
												}));
											}}
										/>
										{/* Dimensions badge */}
										{mpImgDims[mp.id] && (
											<span className="absolute bottom-1.5 left-1.5 bg-slate-700/80 text-white text-[8px] leading-none px-1 py-0.5 rounded font-mono whitespace-nowrap">
												{mpImgDims[mp.id].w}×{mpImgDims[mp.id].h}
											</span>
										)}
										{/* Primary badge */}
										{isPrimary && (
											<span className="absolute top-1.5 right-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-brand text-black">
												<Star className="w-2.5 h-2.5" /> Primary
											</span>
										)}
										{/* Merchant name overlay */}
										<div className="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
											<p className="text-[10px] text-white truncate font-medium">
												{mp.merchant.name}
											</p>
											{mp.currentPrice != null && (
												<p className="text-[10px] text-white/70">
													DKK {mp.currentPrice.toLocaleString("da-DK")}
												</p>
											)}
										</div>
									</button>
								);
							})}
					</div>
				</Section>
			)}

			{/* ────────── Variant Merchant Images ────────── */}
			{(variantImages.length > 0 || loadingVariantImages) && (
				<Section
					title="Variant Images"
					icon={Image}
					badge={
						<span className="text-xs font-normal text-slate-400 ml-1">
							({variantImages.length} images from{" "}
							{new Set(variantImages.map((vi) => vi.variantId)).size} variants)
						</span>
					}
				>
					<p className="text-xs text-slate-400 mb-4">
						Images from variant merchant products. Click to set as primary image
						for this product.
					</p>
					{loadingVariantImages ? (
						<div className="flex items-center justify-center py-8">
							<div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
						</div>
					) : (
						<div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
							{variantImages.map((vi) => {
								const isPrimary =
									product.images.length > 0 &&
									product.images[0].image_url === vi.image;
								return (
									<button
										key={`vi-${vi.mpId}`}
										type="button"
										disabled={settingPrimaryImage}
										onClick={() => {
											if (!isPrimary) handleSetPrimaryImage(vi.image);
										}}
										className={`aspect-square rounded-lg border-2 overflow-hidden bg-white relative group transition-all ${
											isPrimary
												? "border-primary shadow-md shadow-primary/20"
												: "border-slate-200 hover:border-primary/50 hover:shadow-sm"
										} ${settingPrimaryImage ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
									>
										<img
											src={vi.image}
											alt={vi.mpName}
											className="w-full h-full object-contain p-2"
											onLoad={(e) => {
												const img = e.currentTarget;
												setMpImgDims((prev) => ({
													...prev,
													[`v${vi.mpId}`]: {
														w: img.naturalWidth,
														h: img.naturalHeight,
													},
												}));
											}}
										/>
										{/* Dimensions badge */}
										{mpImgDims[`v${vi.mpId}`] && (
											<span className="absolute bottom-1.5 left-1.5 bg-slate-700/80 text-white text-[8px] leading-none px-1 py-0.5 rounded font-mono whitespace-nowrap">
												{mpImgDims[`v${vi.mpId}`].w}×
												{mpImgDims[`v${vi.mpId}`].h}
											</span>
										)}
										{/* Primary badge */}
										{isPrimary && (
											<span className="absolute top-1.5 right-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-brand text-black">
												<Star className="w-2.5 h-2.5" /> Primary
											</span>
										)}
										{/* Variant name label */}
										<span className="absolute top-1.5 left-1.5 bg-amber-100 text-amber-700 text-[8px] leading-none px-1.5 py-0.5 rounded font-semibold truncate max-w-[80%]">
											{vi.variantName}
										</span>
										{/* Merchant overlay on hover */}
										<div className="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
											<p className="text-[10px] text-white truncate font-medium">
												{vi.merchantName}
											</p>
											{vi.currentPrice != null && (
												<p className="text-[10px] text-white/70">
													DKK {vi.currentPrice.toLocaleString("da-DK")}
												</p>
											)}
										</div>
									</button>
								);
							})}
						</div>
					)}
				</Section>
			)}

			{/* ────────── Metadata ────────── */}
			{product.metadata && (
				<Section title="Metadata" icon={Tag}>
					<div className="grid grid-cols-2 gap-4">
						<div>
							<Label>Title</Label>
							<p className="text-sm text-slate-700">
								{product.metadata.title ?? "–"}
							</p>
						</div>
						<div>
							<Label>Popularity Score</Label>
							<p className="text-sm text-slate-700">
								{product.metadata.popularity_score ?? "–"}
							</p>
						</div>
						{product.metadata.description && (
							<div className="col-span-2">
								<Label>Description</Label>
								<p className="text-sm text-slate-600">
									{product.metadata.description}
								</p>
							</div>
						)}
						{product.metadata.tags && (
							<div className="col-span-2">
								<Label>Tags</Label>
								<div className="flex flex-wrap gap-1">
									{product.metadata.tags.split(",").map((t) => (
										<span
											key={t.trim()}
											className="text-[11px] bg-slate-100 text-slate-500 px-2.5 py-0.5 rounded-full"
										>
											{t.trim()}
										</span>
									))}
								</div>
							</div>
						)}
					</div>
				</Section>
			)}
		</div>
	);
};

export default Product;
